// nodeMediaDownload.js — resolve + trigger downloads for media nodes

import { getFileNodeName } from 'utilities/Files';

export const MEDIA_DOWNLOAD_NODE_TYPES = new Set([
  'DOCUMENT',
  'FILE',
  'PHOTO',
  'VIDEO',
]);

function cleanString(value) {
  return String(value || '').trim();
}

function isHttpUrl(value) {
  const raw = cleanString(value);
  if (!raw) return false;
  try {
    const url = new URL(raw);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isHlsReference(value) {
  const raw = cleanString(value).toLowerCase();
  return raw.includes('.m3u8') || raw.includes('/securehlsmanifest');
}

/**
 * @param {object} [node]
 * @returns {boolean}
 */
export function isDownloadableMediaNode(node = {}) {
  return MEDIA_DOWNLOAD_NODE_TYPES.has(cleanString(node?.type).toUpperCase());
}

/**
 * Best-effort download filename for a media node.
 *
 * @param {object} [node]
 * @returns {string}
 */
export function getNodeMediaDownloadName(node = {}) {
  const type = cleanString(node?.type).toUpperCase();

  if (type === 'DOCUMENT') {
    const filename = (
      cleanString(node?.data_document?.filename)
      || cleanString(node?.data_document?.fileName)
      || cleanString(node?.fileName)
      || cleanString(node?.filename)
    );
    if (filename) return filename;

    const title = cleanString(node?.title) || 'document';
    const extension = cleanString(node?.data_document?.extension || node?.extension)
      .replace(/^\./, '')
      .toLowerCase();
    if (extension && !title.toLowerCase().endsWith(`.${extension}`)) {
      return `${title}.${extension}`;
    }
    return title;
  }

  if (type === 'FILE') {
    const filename = getFileNodeName(node)
      || cleanString(node?.fileName)
      || cleanString(node?.filename);
    if (filename) return filename;

    const title = cleanString(node?.title) || 'file';
    const extension = cleanString(node?.data_file?.extension || node?.extension)
      .replace(/^\./, '')
      .toLowerCase();
    if (extension && !title.toLowerCase().endsWith(`.${extension}`)) {
      return `${title}.${extension}`;
    }
    return title;
  }

  if (type === 'PHOTO') {
    return (
      cleanString(node?.fileName)
      || cleanString(node?.data_photo?.filename)
      || `${cleanString(node?.title) || 'photo'}.jpg`
    );
  }

  if (type === 'VIDEO') {
    return (
      cleanString(node?.fileName)
      || cleanString(node?.data_video?.filename)
      || `${cleanString(node?.title) || 'video'}.mp4`
    );
  }

  return cleanString(node?.title) || 'download';
}

/**
 * Raw URL / storage path refs used to resolve a downloadable file.
 *
 * @param {object} [node]
 * @returns {{ url: string|null, storagePath: string|null }}
 */
export function getNodeMediaDownloadRefs(node = {}) {
  const type = cleanString(node?.type).toUpperCase();

  if (type === 'DOCUMENT') {
    return {
      url: cleanString(
        node?.data_document?.securedUrl
        || node?.data_document?.url
        || node?.url,
      ) || null,
      storagePath: cleanString(
        node?.storagePath
        || node?.data_document?.storagePath,
      ) || null,
    };
  }

  if (type === 'FILE') {
    return {
      url: cleanString(
        node?.data_file?.securedUrl
        || node?.data_file?.url
        || node?.url,
      ) || null,
      storagePath: cleanString(
        node?.storagePath
        || node?.data_file?.storagePath,
      ) || null,
    };
  }

  if (type === 'PHOTO') {
    return {
      url: cleanString(node?.url || node?.data_photo?.url) || null,
      storagePath: cleanString(
        node?.storagePath
        || node?.data_photo?.storagePath,
      ) || null,
    };
  }

  if (type === 'VIDEO') {
    // Prefer the original file after HLS transcode (not the .m3u8 manifest).
    const originalDownloadUrl = cleanString(node?.data_video?.downloadUrl);
    if (isHttpUrl(originalDownloadUrl)) {
      return { url: originalDownloadUrl, storagePath: null };
    }

    const url = cleanString(node?.url || node?.data_video?.url) || null;
    const storagePath = cleanString(
      node?.storagePath
      || node?.data_video?.storagePath,
    ) || null;

    if (isHlsReference(url) || isHlsReference(storagePath)) {
      return { url: null, storagePath: null };
    }

    return { url, storagePath };
  }

  return { url: null, storagePath: null };
}

/**
 * @param {object} [node]
 * @returns {Promise<string|null>}
 */
export async function resolveNodeMediaDownloadUrl(node = {}) {
  if (!isDownloadableMediaNode(node)) return null;

  const { url, storagePath } = getNodeMediaDownloadRefs(node);
  if (!url && !storagePath) return null;

  if (url && (url.startsWith('blob:') || url.startsWith('data:'))) {
    return url;
  }

  // Direct https download URL that does not need signing (e.g. video original
  // after transcode, which already includes a Firebase download token).
  if (isHttpUrl(url) && !storagePath) {
    return url;
  }

  // Lazy-load so pure helpers stay unit-testable without Firebase.
  const { resolveFileUrl } = await import('services/storage/StorageAccessService');
  return resolveFileUrl({
    nodeKey: node?.key || null,
    url,
    storagePath,
  });
}

/**
 * Fetch bytes and trigger a browser download. Falls back to opening the URL.
 *
 * @param {object} [node]
 * @returns {Promise<boolean>} Whether a download/open was attempted.
 */
export async function downloadNodeMedia(node = {}) {
  const url = await resolveNodeMediaDownloadUrl(node);
  if (!url) return false;

  const downloadName = getNodeMediaDownloadName(node);

  try {
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
    const blob = await response.blob();

    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = downloadName;
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  }
}
